import { useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Cookies from 'universal-cookie'
import getConfig from 'next/config'
import PdpaUtility from '../utility/pdpaUtility'
import styles from './HeaderNew.module.scss'

const { publicRuntimeConfig } = getConfig()

// Floating "pill" navigation for the new homepage only.
// The shared Header used across the rest of the site (and the old
// homepage /) is left untouched.
//
// Styling was migrated from makeStyles/emotion-JSS to a static CSS Module
// (HeaderNew.module.scss) — zero runtime CSS-in-JS — with the same values
// mirrored from tokens.js. Visual output is unchanged.

const NAV = [
  { labelKey: 'our-services', href: '/service' },
  { labelKey: 'tracking', href: '/tracking' },
  { labelKey: 'about-us', href: '/about' },
  { labelKey: 'faq', href: '/faq' },
  { labelKey: 'contact-us', href: '/contact' },
]

// NEW is the site-wide default now, so every internal nav link can use a
// clean href — no ?new needed. (The OLD shared Header appends ?old so the
// old world stays old.)
const toNew = (href) => href

const ThaiFlag = () => (
  <svg viewBox="0 0 30 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="30" height="18" fill="#F4F5F8" />
    <rect y="0" width="30" height="3" fill="#A51931" />
    <rect y="6" width="30" height="6" fill="#2D2A4A" />
    <rect y="15" width="30" height="3" fill="#A51931" />
  </svg>
)

const UKFlag = () => (
  <svg viewBox="0 0 30 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="30" height="18" fill="#012169" />
    <path d="M0 0 L30 18 M30 0 L0 18" stroke="#fff" strokeWidth="3.6" />
    <path d="M0 0 L30 18 M30 0 L0 18" stroke="#C8102E" strokeWidth="2" />
    <rect x="12" width="6" height="18" fill="#fff" />
    <rect y="6" width="30" height="6" fill="#fff" />
    <rect x="13.5" width="3" height="18" fill="#C8102E" />
    <rect y="7.5" width="30" height="3" fill="#C8102E" />
  </svg>
)

const ChevronDown = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const LOCALES = [
  { code: 'th', labelKey: 'lang-name-th', Flag: ThaiFlag },
  { code: 'en', labelKey: 'lang-name-en', Flag: UKFlag },
]

const HeaderNew = () => {
  const { t } = useTranslation('header')
  const Router = useRouter()
  const [menuOpen, setMenuOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [memberOpen, setMemberOpen] = useState(false)
  const [user, setUser] = useState(null)
  const cookies = new Cookies()

  const isActive = (href) => Router.asPath.split('?')[0] === href
  const isTh = Router.locale !== 'en'
  const currentCode = isTh ? 'th' : 'en'

  // Client-only login detection — mirrors the OLD design (Layout.js):
  // a jwt_token in localStorage means logged in, and shp_user holds the
  // member object under `.user`. SSR renders the logged-out button; the
  // client swaps to the member area after mount (no hydration crash).
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('jwt_token')) {
        const raw = localStorage.getItem('shp_user')
        if (raw) setUser(JSON.parse(raw)?.user || null)
      }
    } catch (e) {
      /* malformed shp_user — stay logged-out */
    }
  }, [])

  // Close the language dropdown on any outside click.
  useEffect(() => {
    if (!langOpen) return undefined
    const close = () => setLangOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [langOpen])

  // Close the member dropdown on any outside click (same pattern as lang).
  useEffect(() => {
    if (!memberOpen) return undefined
    const close = () => setMemberOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [memberOpen])

  const changeLang = (lng) => {
    cookies.set('SHIPPOP_LOCALE', lng, { path: '/' })
    const path = Router.asPath
    Router.push(path, path, { locale: lng })
  }

  // Mirror of Header.js handleLogout: hit the SSO logout endpoint with the
  // member's randToken, clear the local session + PDPA cookies, then bounce
  // through /check-auth back to the current page.
  const handleLogout = (e = null) => {
    if (e) e.preventDefault()
    if (typeof window === 'undefined' || !localStorage.getItem('jwt_token')) return
    let randToken = ''
    try {
      randToken = JSON.parse(localStorage.getItem('shp_user')).user.randToken
    } catch (err) {
      return
    }
    const currentUrl = `${publicRuntimeConfig.siteDomain}${Router.pathname}`
    const checkAuthUrl = `${publicRuntimeConfig.siteDomain}/check-auth?destUrl=${currentUrl}`
    const logoutUrl = `${
      publicRuntimeConfig.memberService
    }/sso/logout?logoutToken=${randToken}&cbUrl=${encodeURIComponent(
      checkAuthUrl
    )}`
    localStorage.removeItem('shp_user')
    localStorage.removeItem('jwt_token')
    PdpaUtility.clearAllLoginCookies()
    window.location.replace(logoutUrl)
  }

  const avatarSrc =
    user && user.facebook_id
      ? `https://graph.facebook.com/${user.facebook_id}/picture?height=200&width=200`
      : '/static/img/icons/user-profile.svg'
  const displayName = user
    ? `${user.name.slice(0, 20)}${user.name.length > 20 ? '...' : ''}`
    : ''
  const memberMenus = [
    { labelKey: 'manage-shipment', href: `${publicRuntimeConfig.appShippop}/` },
    {
      labelKey: 'member-dashboard',
      href: `${publicRuntimeConfig.appShippop}/edit-profile/`,
    },
  ]

  return (
    <header className={styles.root}>
      <div className={styles.pill}>
        <Link href="/" passHref>
          <a className={styles.logoLink} aria-label={t('logo-aria')}>
            <img
              src="/static/img/sp-logo.svg"
              alt="SHIPPOP"
              className={styles.logo}
              width="167"
              height="30"
              decoding="async"
            />
          </a>
        </Link>

        <nav className={styles.nav}>
          {NAV.map((item) => (
            <Link href={toNew(item.href)} passHref key={item.href}>
              <a
                className={`${styles.navLink} ${
                  isActive(item.href) ? styles.navLinkActive : ''
                }`}
              >
                {t(item.labelKey)}
              </a>
            </Link>
          ))}
        </nav>

        <div className={styles.actions}>
          <Link href="/business" passHref>
            <a className={styles.corporateBtn}>{t('corporate')}<span className={styles.corporateBadge}>API</span></a>
          </Link>
          {user ? (
            <div
              className={styles.memberWrap}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={styles.memberTrigger}
                onClick={() => setMemberOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={memberOpen}
                type="button"
              >
                <img
                  src={avatarSrc}
                  alt="user"
                  className={styles.memberAvatar}
                  width="32"
                  height="32"
                  decoding="async"
                />
                <span className={styles.memberName}>{displayName}</span>
                <ChevronDown
                  className={`${styles.chevron} ${
                    memberOpen ? styles.chevronOpen : ''
                  }`}
                />
              </button>
              {memberOpen && (
                <div className={styles.memberMenu} role="menu">
                  {memberMenus.map((m) => (
                    <a
                      key={m.labelKey}
                      href={m.href}
                      className={styles.memberMenuItem}
                      role="menuitem"
                    >
                      {t(m.labelKey)}
                    </a>
                  ))}
                  <button
                    className={`${styles.memberMenuItem} ${styles.memberMenuDanger}`}
                    onClick={handleLogout}
                    role="menuitem"
                    type="button"
                  >
                    {t('log-out')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link href="/login" passHref>
              <a className={styles.login}>{t('login')}</a>
            </Link>
          )}
          <div
            className={styles.langWrap}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.langTrigger}
              onClick={() => setLangOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              aria-label={t('change-language-aria')}
              type="button"
            >
              <span className={styles.flagBox}>
                {isTh ? <ThaiFlag /> : <UKFlag />}
              </span>
              <ChevronDown
                className={`${styles.chevron} ${langOpen ? styles.chevronOpen : ''}`}
              />
            </button>
            {langOpen && (
              <div className={styles.langMenu} role="listbox">
                {LOCALES.map(({ code, labelKey, Flag }) => (
                  <button
                    key={code}
                    role="option"
                    aria-selected={code === currentCode}
                    className={`${styles.langMenuItem} ${
                      code === currentCode ? styles.langMenuItemActive : ''
                    }`}
                    onClick={() => {
                      changeLang(code)
                      setLangOpen(false)
                    }}
                    type="button"
                  >
                    <span className={styles.flagBox}>
                      <Flag />
                    </span>
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className={styles.burger}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={t('menu-aria')}
            aria-expanded={menuOpen}
            type="button"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className={styles.mobileMenu}>
          {NAV.map((item) => (
            <Link href={toNew(item.href)} passHref key={item.href}>
              <a
                className={styles.mobileLink}
                onClick={() => setMenuOpen(false)}
              >
                {t(item.labelKey)}
              </a>
            </Link>
          ))}
          {user ? (
            <>
              <div className={styles.mobileMember}>
                <img
                  src={avatarSrc}
                  alt="user"
                  className={styles.mobileMemberAvatar}
                  width="36"
                  height="36"
                  decoding="async"
                />
                <span className={styles.mobileMemberName}>{displayName}</span>
              </div>
              {memberMenus.map((m) => (
                <a
                  key={m.labelKey}
                  href={m.href}
                  className={styles.mobileLink}
                  onClick={() => setMenuOpen(false)}
                >
                  {t(m.labelKey)}
                </a>
              ))}
              <a
                className={`${styles.mobileLink} ${styles.mobileLogout}`}
                onClick={handleLogout}
              >
                {t('log-out')}
              </a>
            </>
          ) : (
            <Link href="/login" passHref>
              <a
                className={styles.mobileLink}
                onClick={() => setMenuOpen(false)}
              >
                {t('login')}
              </a>
            </Link>
          )}
          <div className={styles.mobileLang}>
            <button
              className={`${styles.langItem} ${isTh ? styles.langActive : ''}`}
              onClick={() => changeLang('th')}
              type="button"
            >
              {t('lang-short-th')}
            </button>
            <span className={styles.langSep} />
            <button
              className={`${styles.langItem} ${!isTh ? styles.langActive : ''}`}
              onClick={() => changeLang('en')}
              type="button"
            >
              {t('lang-short-en')}
            </button>
          </div>
          <Link href="/business" passHref>
            <a
              className={styles.mobileCorporate}
              onClick={() => setMenuOpen(false)}
            >
              {t('corporate')}
            </a>
          </Link>
        </div>
      )}
    </header>
  )
}

export default HeaderNew
